import React, { useEffect, useContext, useState } from "react";
import {
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  FormControl,
} from "@mui/material";
import { getColors } from "../../themes/theme";
import { ThemeContext } from "../../context/ThemeContext";
import { useForm } from "react-hook-form";
import { SelectImageDialog } from "./SelectImageDialog";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import axios from "axios";
import { ExerciseOriginItem } from "./ExerciseOriginItem";
import { TreatmentOriginItem } from "./TreatmentOriginItem";
import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import { EditTreatmentsDialog } from "./EditTreatmentsDialog";
import { EditExercisesDialog } from "./EditExercisesDialog";

export const CopyConditionDialog = ({
  open,
  onClose,
  onSave,
  conditionData,
}) => {
  const methods = useForm({
    area: conditionData?.area,
    acondition: conditionData?.acondition,
    description: conditionData?.description,
    bmname: conditionData?.bmname,
  });
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;
  const { darkMode } = useContext(ThemeContext);
  const { token } = useAuthContext();
  const [openImageDialog, setOpenImageDialog] = useState(false); // State to manage image dialog visibility
  const [selectedImage, setSelectedImage] = useState(conditionData?.bmname);
  const [exercises, setExercises] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [allTreatments, setAllTreatments] = useState([]);
  const [openTreatmentsDialog, setOpenTreatmentsDialog] = useState(false);
  const [selectedTreatmentIds, setSelectedTreatmentIds] = useState([]);
  const [openExercisesDialog, setOpenExercisesDialog] = useState(false);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState([]);

  const handleImageSelect = (bmname) => {
    setSelectedImage(bmname); // Update the selected image
    setValue("bmname", bmname); // Update the form value
  };

  useEffect(() => {
    if (conditionData?.treatmentlist) {
      setSelectedTreatmentIds(
        conditionData.treatmentlist
          .split(",")
          .map(Number)
          .filter((id) => id !== 0)
      );
    }
  }, [conditionData]);

  useEffect(() => {
    if (conditionData?.exerciselist) {
      setSelectedExerciseIds(
        conditionData.exerciselist
          .split(",")
          .map(Number)
          .filter((id) => id !== 0)
      );
    }
  }, [conditionData]);

  useEffect(() => {
    if (conditionData) {
      setValue("area", conditionData?.area);
      setValue("acondition", conditionData?.acondition);
      setValue("description", conditionData?.description);
      setValue("bmname", conditionData?.bmname);
      setSelectedImage(conditionData?.bmname);
    }
  }, [conditionData, setValue]);

  useEffect(() => {
    const fetchExercises = async () => {
      if (!conditionData?.id) return;

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/condition/${conditionData.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const exerciseIDList = response.data.exerciselist
          .split(",")
          .map(Number)
          .filter((id) => id !== 0);

        if (exerciseIDList.length > 0) {
          const exerciseResponse = await axios.post(
            `${process.env.REACT_APP_API_URL}/exercise/getexercises`,
            {
              ids: exerciseIDList,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setExercises(exerciseResponse.data);
          const res1 = await axios.get(
            `${process.env.REACT_APP_API_URL}/exercise`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          setAllExercises(res1.data);
        }
      } catch (err) {
        console.error("Error fetching exercises:", err);
      }
    };

    fetchExercises();
  }, [conditionData?.id, token]);

  useEffect(() => {
    const fetchTreatments = async () => {
      if (!token || !conditionData?.id) return;

      try {
        const treatmentResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/treatment`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const conditionResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/condition/${conditionData.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const treatmentList = `,${conditionResponse.data.treatmentlist},`;
        const matchingTreatments = treatmentResponse.data.filter((treatment) =>
          treatmentList.includes(`,${treatment.id},`)
        );
        setTreatments(matchingTreatments);
        setAllTreatments(treatmentResponse.data);
      } catch (err) {
        console.error("Error fetching treatments:", err);
      }
    };

    fetchTreatments();
  }, [token, conditionData?.id]);

  let img;
  if (selectedImage !== null && selectedImage !== undefined) {
    img = require(`../../assets/condition/${selectedImage}.png`);
  }

  const onSubmit = (data) => {
    const editedConditionData = {
      ...conditionData,
      area: data.area,
      acondition: data.acondition,
      description: data.description,
      bmname: data.bmname,
      treatmentlist:
        selectedTreatmentIds.length > 0
          ? "," + treatments.map((treatment) => treatment.id).join(",")
          : null,
      exerciselist:
        selectedExerciseIds.length > 0
          ? "," + exercises.map((exercise) => exercise.id).join(",")
          : null,
    };
    onSave(editedConditionData);
    onClose();
  };

  const handleExerciseClicked = () => {
    setOpenExercisesDialog(true);
  };

  const handleTreatmentClicked = () => {
    setOpenTreatmentsDialog(true);
  };

  const handleSaveExercises = (updatedExerciseIds) => {
    setSelectedExerciseIds(updatedExerciseIds);
    const updatedExercises = allExercises.filter((exercise) =>
      updatedExerciseIds.includes(exercise.id)
    );
    setExercises(updatedExercises);
  };

  const handleSaveTreatments = (updatedTreatmentIds) => {
    setSelectedTreatmentIds(updatedTreatmentIds);
    const updatedTreatments = allTreatments.filter((treatment) =>
      updatedTreatmentIds.includes(treatment.id)
    );
    setTreatments(updatedTreatments);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <DialogTitle sx={{ fontSize: "30px", pb: 0 }}>
          Copy Condition
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{ pt: 0 }}>
            <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              columnGap={3}
            >
              <FormControl fullWidth margin="normal">
                <TextField
                  label="Area Name"
                  placeholder="Enter Area Name"
                  {...register("area", {
                    required: "Area Name is required",
                  })}
                  error={!!errors.area}
                  helperText={errors.area?.message}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  label="Condition Name"
                  placeholder="Enter Condition Name"
                  {...register("acondition", {
                    required: "Condition Name is required",
                  })}
                  error={!!errors.acondition}
                  helperText={errors.acondition?.message}
                />
              </FormControl>
            </Box>

            <Box display="flex" sx={{ mt: 3 }}>
              <img
                src={img}
                alt="img"
                style={{ width: "300px", height: "200px", cursor: "pointer" }}
                onClick={() => setOpenImageDialog(true)} // Open the image selection dialog
              />
              <FormControl fullWidth margin="normal" sx={{ ml: 4 }}>
                <TextField
                  label="Description"
                  multiline
                  rows={4}
                  placeholder="Condition Description"
                  {...register("description", {
                    required: "Description is required",
                  })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </FormControl>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              sx={{ mt: 3, alignItems: "center" }}
            >
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 500,
                  color: darkMode
                    ? getColors.drawerTextDark
                    : getColors.thumbnailTextLight,
                }}
              >
                Treatments
              </Typography>
              <Button
                variant="contained"
                startIcon={
                  <ChangeCircleOutlinedIcon
                    style={{ fontSize: 28, marginRight: 8 }}
                  />
                }
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "20px",
                  px: 4,
                  py: 1,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleTreatmentClicked}
              >
                Change
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={2}
              alignItems={"center"}
              sx={{ mt: 3 }}
            >
              {treatments.map((treatment, id) => {
                // const treatment = treatments.find((t) => t.id === id);
                return <TreatmentOriginItem key={id} {...treatment} />;
              })}
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              sx={{ mt: 5, alignItems: "center" }}
            >
              <Typography
                sx={{
                  fontSize: "24px",
                  fontWeight: 500,
                  color: darkMode
                    ? getColors.drawerTextDark
                    : getColors.thumbnailTextLight,
                }}
              >
                Exercises
              </Typography>
              <Button
                variant="contained"
                startIcon={
                  <ChangeCircleOutlinedIcon
                    style={{ fontSize: 28, marginRight: 8 }}
                  />
                }
                sx={{
                  fontFamily: "Poppins",
                  fontSize: "20px",
                  px: 4,
                  py: 1,
                  background: darkMode ? "#2D2E30" : "#E6E6E6",
                  color: darkMode
                    ? getColors.primaryTextDark
                    : getColors.secondaryTextLight,
                  "&:hover": {
                    color: getColors.primaryTextDark,
                  },
                }}
                onClick={handleExerciseClicked}
              >
                Change
              </Button>
            </Box>
            <Box
              display="grid"
              gridTemplateColumns="repeat(3, 1fr)"
              gap={2}
              alignItems={"center"}
              sx={{ mt: 3 }}
            >
              {exercises.map((exercise, index) => (
                <ExerciseOriginItem key={index} {...exercise} />
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
            <Button
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                color: darkMode
                  ? getColors.appBarBgLight
                  : getColors.appBarBgDark,
                px: 6,
                py: "11px",
                textAlign: "center",
                "&: hover": {
                  background: "none",
                  color: getColors.primaryMain,
                },
                mr: 2,
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: "5px",
                fontFamily: "Poppins",
                fontSize: "18px",
                fontWeight: 500,
                px: 6,
                py: "11px",
                textAlign: "center",
              }}
            >
              Add
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <SelectImageDialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        onSelectImage={handleImageSelect}
      />
      <EditTreatmentsDialog
        open={openTreatmentsDialog}
        onClose={() => setOpenTreatmentsDialog(false)}
        token={token}
        selectedTreatmentIds={selectedTreatmentIds}
        onSave={handleSaveTreatments}
      />
      <EditExercisesDialog
        open={openExercisesDialog}
        onClose={() => setOpenExercisesDialog(false)}
        token={token}
        selectedExerciseIds={selectedExerciseIds}
        onSave={handleSaveExercises}
      />
    </>
  );
};
